import React, { useState, useEffect } from 'react';
import { whatsappApi, WhatsAppAlert, WhatsAppAlertCreate } from '../api/whatsapp';
import { organizationApi, Organization } from '../api/organizations';
import { storeApi, Store } from '../api/stores';

interface WhatsAppProps {
  userInfo?: any;
}

// Cap how many checkboxes we actually render. The search box is the real way to
// find a store, and drawing thousands of rows locks up the browser.
const STORE_RENDER_LIMIT = 200;

// Load every store for a single organization. Uses the per-org endpoint, which
// is unpaginated -- the global /stores list is capped at 100 rows shared across
// all organizations, so it silently truncates large tenants.
const useOrgStores = (orgId?: string) => {
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId) {
      setStores([]);
      setError(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await storeApi.listByOrg(orgId);
        if (!cancelled) setStores(data);
      } catch (err) {
        console.error(`Error loading stores for org ${orgId}:`, err);
        if (!cancelled) {
          setStores([]);
          setError('Failed to load stores for this organization.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  return { stores, loading, error };
};

const filterStores = (stores: Store[], searchTerm: string): Store[] => {
  if (!searchTerm) return stores;
  const term = searchTerm.toLowerCase();
  return stores.filter(
    (store) =>
      store.store_name.toLowerCase().includes(term) ||
      store.store_id.toLowerCase().includes(term)
  );
};

const WhatsApp: React.FC<WhatsAppProps> = ({ userInfo }) => {
  const [alerts, setAlerts] = useState<WhatsAppAlert[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showManageStoresModal, setShowManageStoresModal] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<WhatsAppAlert | null>(null);
  const [formData, setFormData] = useState<WhatsAppAlertCreate>({
    phone_number: '',
    service: 'discipline_alerts',
    store_ids: [],
    org_id: userInfo?.org_id || '',
    is_active: true,
    granted_by: 'admin'
  });
  const [searchTerm, setSearchTerm] = useState<string>('');

  useEffect(() => {
    loadData();
  }, []);

  // Update formData when userInfo changes
  useEffect(() => {
    if (userInfo?.org_id && !userInfo?.is_admin) {
      setFormData(prev => ({
        ...prev,
        org_id: userInfo.org_id
      }));
    }
  }, [userInfo]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [alertsData, orgsData] = await Promise.all([
        whatsappApi.list(),
        organizationApi.list()
      ]);
      setAlerts(alertsData);
      setOrganizations(orgsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent, dataToSubmit?: WhatsAppAlertCreate) => {
    e.preventDefault();
    try {
      const submitData = dataToSubmit || formData;
      console.log('Creating WhatsApp alert with formData:', submitData);
      await whatsappApi.create(submitData);
      setShowCreateModal(false);
      // Reset form but preserve org_id for non-admin users
      const resetOrgId = userInfo?.is_admin ? '' : userInfo?.org_id || '';
      setFormData({
        phone_number: '',
        service: 'discipline_alerts',
        store_ids: [],
        org_id: resetOrgId,
        is_active: true,
        granted_by: 'admin'
      });
      loadData();
    } catch (error: any) {
      console.error('Error creating WhatsApp alert:', error);
      console.error('Error details:', error.response?.data);
      alert(error.response?.data?.detail || 'Failed to create WhatsApp alert');
    }
  };

  const handleDisable = async (id: number) => {
    if (window.confirm('Are you sure you want to disable this WhatsApp alert?')) {
      try {
        await whatsappApi.delete(id);
        loadData();
      } catch (error) {
        console.error('Error disabling WhatsApp alert:', error);
      }
    }
  };

  const handleEnable = async (id: number) => {
    if (window.confirm('Are you sure you want to enable this WhatsApp alert?')) {
      try {
        await whatsappApi.update(id, { is_active: true });
        loadData();
      } catch (error) {
        console.error('Error enabling WhatsApp alert:', error);
      }
    }
  };

  const openCreateModal = () => {
    // Ensure org_id is set correctly when opening the modal
    const initialOrgId = userInfo?.is_admin ? '' : userInfo?.org_id || '';
    setFormData({
      phone_number: '',
      service: 'discipline_alerts',
      store_ids: [],
      org_id: initialOrgId,
      is_active: true,
      granted_by: 'admin'
    });
    setShowCreateModal(true);
  };

  const openManageStoresModal = (alert: WhatsAppAlert) => {
    setSelectedAlert(alert);
    setShowManageStoresModal(true);
  };

  const handleStoresUpdate = async (selectedStores: string[]) => {
    if (!selectedAlert) return;

    try {
      await whatsappApi.updateStores(selectedAlert.phone_number, selectedAlert.service, {
        store_ids: selectedStores
      });

      setShowManageStoresModal(false);
      loadData();
    } catch (error) {
      console.error('Error updating stores:', error);
      alert('Failed to update stores');
    }
  };

  // Filter alerts based on search term
  const filteredAlerts = alerts.filter(alert => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const org = organizations.find(o => o.org_id === alert.org_id);
    return (
      alert.phone_number.toLowerCase().includes(searchLower) ||
      alert.service.toLowerCase().includes(searchLower) ||
      alert.org_id.toLowerCase().includes(searchLower) ||
      (org && org.name.toLowerCase().includes(searchLower))
    );
  });

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-3xl font-bold text-gray-900">WhatsApp Alerts</h1>
          <p className="mt-2 text-gray-600">
            Manage WhatsApp alert recipients and their store assignments
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            onClick={openCreateModal}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Add Phone Number
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mt-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search by phone number or organization..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>
      </div>

      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Phone Number
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Service
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Organization
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stores
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAlerts.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                        {searchTerm ? 'No alerts found matching your search.' : 'No WhatsApp alerts found.'}
                      </td>
                    </tr>
                  ) : (
                    filteredAlerts.map((alert) => {
                      const org = organizations.find(o => o.org_id === alert.org_id);
                      return (
                        <tr key={alert.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {alert.phone_number}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              alert.service === 'discipline_alerts'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-purple-100 text-purple-800'
                            }`}>
                              {alert.service.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {org?.name || alert.org_id}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {alert.store_ids.length} stores
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              alert.is_active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {alert.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(alert.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => openManageStoresModal(alert)}
                                className="text-green-600 hover:text-green-900"
                              >
                                Manage Stores
                              </button>
                              {alert.is_active ? (
                                <button
                                  onClick={() => handleDisable(alert.id)}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  Disable
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleEnable(alert.id)}
                                  className="text-blue-600 hover:text-blue-900"
                                >
                                  Enable
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateWhatsAppModal
          formData={formData}
          setFormData={setFormData}
          organizations={organizations}
          userInfo={userInfo}
          onSubmit={handleCreate}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {/* Manage Stores Modal */}
      {showManageStoresModal && selectedAlert && (
        <ManageStoresModal
          alert={selectedAlert}
          onSave={handleStoresUpdate}
          onClose={() => setShowManageStoresModal(false)}
        />
      )}
    </div>
  );
};

// Create WhatsApp Modal Component
interface CreateWhatsAppModalProps {
  formData: WhatsAppAlertCreate;
  setFormData: (data: WhatsAppAlertCreate) => void;
  organizations: Organization[];
  userInfo?: any;
  onSubmit: (e: React.FormEvent, data?: WhatsAppAlertCreate) => void;
  onClose: () => void;
}

const CreateWhatsAppModal: React.FC<CreateWhatsAppModalProps> = ({
  formData,
  setFormData,
  organizations,
  userInfo,
  onSubmit,
  onClose
}) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedStores, setSelectedStores] = useState<Set<string>>(new Set());
  const { stores, loading: storesLoading, error: storesError } = useOrgStores(formData.org_id);

  // Stores already come back scoped to the org, so only search filtering is left
  const filteredStores = filterStores(stores, searchTerm);
  const visibleStores = filteredStores.slice(0, STORE_RENDER_LIMIT);

  const allFilteredSelected = filteredStores.length > 0 &&
    filteredStores.every(store => selectedStores.has(store.store_id));

  const toggleStore = (storeId: string, checked: boolean) => {
    setSelectedStores(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(storeId);
      } else {
        next.delete(storeId);
      }
      return next;
    });
  };

  // Applies to every store matching the current search, not just the rendered ones
  const handleSelectAll = () => {
    setSelectedStores(prev => {
      const next = new Set(prev);
      filteredStores.forEach(store => {
        if (allFilteredSelected) {
          next.delete(store.store_id);
        } else {
          next.add(store.store_id);
        }
      });
      return next;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Create updated formData with selected stores
    const updatedFormData = {
      ...formData,
      store_ids: Array.from(selectedStores)
    };
    // Update parent formData
    setFormData(updatedFormData);
    // Call parent submit handler with updated data directly
    onSubmit(e, updatedFormData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white max-h-[80vh] overflow-y-auto">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Add WhatsApp Alert</h3>
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                type="text"
                required
                placeholder="919227702656"
                value={formData.phone_number}
                onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Service Type
              </label>
              <select
                required
                value={formData.service}
                onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="discipline_alerts">Discipline Alerts</option>
                <option value="offline_alerts">Offline Alerts</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Organization
              </label>
              {userInfo?.is_admin ? (
                <select
                  required
                  value={formData.org_id}
                  onChange={(e) => {
                    setFormData({ ...formData, org_id: e.target.value });
                    setSelectedStores(new Set()); // Reset selected stores when org changes
                    setSearchTerm('');
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Organization</option>
                  {organizations.map((org) => (
                    <option key={org.org_id} value={org.org_id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={userInfo?.org_name || formData.org_id}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                />
              )}
            </div>

            {/* Store Selection */}
            {formData.org_id && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Stores ({selectedStores.size} selected)
                </label>

                {/* Search Box */}
                <div className="mb-3">
                  <input
                    type="text"
                    placeholder="Search stores..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>

                {/* Select All Checkbox */}
                {filteredStores.length > 0 && (
                  <div className="mb-3 pb-2 border-b border-gray-200">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={allFilteredSelected}
                        onChange={handleSelectAll}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-800">
                        Select All ({filteredStores.length} stores)
                      </span>
                    </label>
                  </div>
                )}

                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {storesLoading ? (
                    <div className="text-sm text-gray-500 italic text-center py-4">
                      Loading stores...
                    </div>
                  ) : storesError ? (
                    <div className="text-sm text-red-600 text-center py-4">{storesError}</div>
                  ) : filteredStores.length === 0 ? (
                    <div className="text-sm text-gray-500 italic text-center py-4">
                      {searchTerm ? 'No stores found matching your search.' : 'No stores available for this organization.'}
                    </div>
                  ) : (
                    visibleStores.map((store) => (
                      <label key={store.store_id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedStores.has(store.store_id)}
                          onChange={(e) => toggleStore(store.store_id, e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          {store.store_name} ({store.store_id})
                        </span>
                      </label>
                    ))
                  )}
                </div>

                {filteredStores.length > visibleStores.length && (
                  <p className="mt-2 text-xs text-gray-500 italic">
                    Showing {visibleStores.length} of {filteredStores.length} stores - type above to narrow the list.
                  </p>
                )}
              </div>
            )}

            <div className="flex justify-end space-x-2 mt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={selectedStores.size === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Create ({selectedStores.size} stores)
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Manage Stores Modal Component
interface ManageStoresModalProps {
  alert: WhatsAppAlert;
  onSave: (selectedStores: string[]) => void;
  onClose: () => void;
}

const ManageStoresModal: React.FC<ManageStoresModalProps> = ({
  alert,
  onSave,
  onClose
}) => {
  const [selectedStores, setSelectedStores] = useState<Set<string>>(() => new Set(alert.store_ids));
  const [searchTerm, setSearchTerm] = useState<string>('');
  const { stores, loading: storesLoading, error: storesError } = useOrgStores(alert.org_id);

  // Stores already come back scoped to the org, so only search filtering is left
  const filteredStores = filterStores(stores, searchTerm);
  const visibleStores = filteredStores.slice(0, STORE_RENDER_LIMIT);

  const allFilteredSelected = filteredStores.length > 0 &&
    filteredStores.every(store => selectedStores.has(store.store_id));

  const toggleStore = (storeId: string, checked: boolean) => {
    setSelectedStores(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(storeId);
      } else {
        next.delete(storeId);
      }
      return next;
    });
  };

  // Applies to every store matching the current search, not just the rendered ones
  const handleSelectAll = () => {
    setSelectedStores(prev => {
      const next = new Set(prev);
      filteredStores.forEach(store => {
        if (allFilteredSelected) {
          next.delete(store.store_id);
        } else {
          next.add(store.store_id);
        }
      });
      return next;
    });
  };

  const handleSave = () => {
    onSave(Array.from(selectedStores));
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Manage Stores for {alert.phone_number}
          </h3>

          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-3">
              Select the stores this phone number should receive alerts for:
            </p>

            {/* Search Box */}
            <div className="mb-3">
              <input
                type="text"
                placeholder="Search stores by name or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>

            {/* Select All Checkbox */}
            {filteredStores.length > 0 && (
              <div className="mb-3 pb-2 border-b border-gray-200">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-800">
                    Select All ({filteredStores.length} stores)
                  </span>
                </label>
              </div>
            )}

            <div className="space-y-2 max-h-64 overflow-y-auto">
              {storesLoading ? (
                <div className="text-sm text-gray-500 italic text-center py-4">
                  Loading stores...
                </div>
              ) : storesError ? (
                <div className="text-sm text-red-600 text-center py-4">{storesError}</div>
              ) : filteredStores.length === 0 ? (
                <div className="text-sm text-gray-500 italic text-center py-4">
                  {searchTerm ? 'No stores found matching your search.' : 'No stores available.'}
                </div>
              ) : (
                visibleStores.map((store) => (
                  <label key={store.store_id} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedStores.has(store.store_id)}
                      onChange={(e) => toggleStore(store.store_id, e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      {store.store_name} ({store.store_id})
                    </span>
                  </label>
                ))
              )}
            </div>

            {filteredStores.length > visibleStores.length && (
              <p className="mt-2 text-xs text-gray-500 italic">
                Showing {visibleStores.length} of {filteredStores.length} stores - type above to narrow the list.
              </p>
            )}
          </div>

          <div className="flex justify-end space-x-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              Save ({selectedStores.size} stores)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsApp;
